import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import DealerCardUI from './DealerCard';
import GoogleMapRefContext from '../GoogleMapRefContext';
import animateZoomToLevel from '../../utils/googleMap/animateZoomToLevel';
import parseCoordinatesToNumbers from '../../utils/googleMap/parseCoordinatesToNumbers';
import { DEALER_DETAILS, PREFIX_TYPE, SCHEDULE_SERVICE, pushGtmDealerCardInfo } from '../../utils/gtmTags';
import { MAP_ZOOM_DURATION_MODAL_VIEW, MAP_ZOOM_LEVEL_MODAL_VIEW } from '../../utils/constants';
import useJSSComponentContext from '../../hooks/useJSSComponentContext';
import useDealerCookie from '../../hooks/useDealerCookie';
import { useVariant } from '../../context/VariantContext';
import dealerPropTypes from '../../utils/interfaces';
import getSalesDeptContactInformation from '../../utils/getSalesDeptContactInformation';
import formatDealerAddress from '../../utils/formatDealerAddress';

const DealerCardMap = ({ dealer, setHoveredDealer, setSelectedDealer, setDealerModalOpen, ...props }) => {
  const variant = useVariant();

  const setDealerCookie = useDealerCookie();
  const { current: mapRef } = useContext(GoogleMapRefContext);

  const { name, code, location, departments, drivingDistance } = dealer;

  // get dealer sales department contact details
  const dealerContactInformation = getSalesDeptContactInformation(departments);
  const dealerAddress = formatDealerAddress(location);

  const handleDealerDetailsModalOpen = useCallback(() => {
    setSelectedDealer(dealer);
    setDealerModalOpen(true);
    mapRef?.panTo?.(parseCoordinatesToNumbers(dealer?.location?.coordinates));
    animateZoomToLevel(mapRef, MAP_ZOOM_LEVEL_MODAL_VIEW, MAP_ZOOM_DURATION_MODAL_VIEW);
    window.scrollTo(0, 0);
    pushGtmDealerCardInfo(PREFIX_TYPE + DEALER_DETAILS, name, code);
    setDealerCookie({
      dealerCode: code,
      dealerName: name,
      dealerContact: dealerContactInformation?.phone,
      dealerEmail: dealerContactInformation?.email,
      dealerWebsite: dealerContactInformation?.website,
      dealerAddress,
      dealerDistance: drivingDistance,
      evDealer: dealer?.evDealer,
    });
  }, [code, drivingDistance]);

  const handleDealerCardClick = useCallback(() => {
    setSelectedDealer(dealer);
    mapRef?.panTo?.(parseCoordinatesToNumbers(dealer?.location?.coordinates));
  }, [code, drivingDistance]);

  const handleScheduleServiceClick = useCallback(() => {
    pushGtmDealerCardInfo(PREFIX_TYPE + SCHEDULE_SERVICE, name, code);
  }, [code]);

  const handleDealerCardMouseEnter = useCallback(() => setHoveredDealer(dealer), [code]);
  const handleDealerCardMouseLeave = useCallback(() => setHoveredDealer(null), []);

  const { componentName, DealerCardComponent, fields } = useJSSComponentContext();

  if (componentName && DealerCardComponent) {
    return (
      <DealerCardComponent
        fields={fields}
        dealer={dealer}
        handleDealerCardMouseEnter={handleDealerCardMouseEnter}
        handleDealerCardMouseLeave={handleDealerCardMouseLeave}
        handleDealerCardClick={handleDealerCardClick}
        setDealerCookie={setDealerCookie}
        variant={variant}
        {...props}
      />
    );
  }

  return (
    <DealerCardUI
      dealer={dealer}
      handleDealerCardClick={handleDealerCardClick}
      handleDealerDetailsModalOpen={handleDealerDetailsModalOpen}
      handleScheduleServiceClick={handleScheduleServiceClick}
      handleDealerCardMouseEnter={handleDealerCardMouseEnter}
      handleDealerCardMouseLeave={handleDealerCardMouseLeave}
      variant={variant}
      {...props}
    />
  );
};

DealerCardMap.propTypes = {
  dealer: dealerPropTypes.isRequired,
  distance: PropTypes.number,
  setHoveredDealer: PropTypes.func.isRequired,
  setSelectedDealer: PropTypes.func.isRequired,
  setDealerModalOpen: PropTypes.func.isRequired,
  isCardSelected: PropTypes.bool.isRequired,
  isCardHovered: PropTypes.bool.isRequired,
  showSelectDealerCTA: PropTypes.bool.isRequired,
  variant: PropTypes.string,
  urlParams: PropTypes.shape({
    queryParams: PropTypes.string,
    currentURLName: PropTypes.string.isRequired,
  }),
};

export default DealerCardMap;
